import React, { useState } from "react";
import { Link } from "react-router-dom";
import SEOMeta from "../components/SEOMeta";

const ContactUs: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"success" | "error" | null>(null);

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Contact Us",
    description: "Contact CyberAnnals - Reach out to us with questions, feedback, or suggestions about our cybersecurity archive and educational content.",
    url: "https://cyberannals.com/contact",
    about: {
      "@type": "Organization",
      name: "CyberAnnals",
      description: "Comprehensive historical archive of cyber attacks dedicated to documenting and preserving the chronicles of cyber attacks throughout history."
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // In a real implementation, this would send the data to a backend service
      // Since this is a frontend-only application for Cloudflare Pages,
      // we'll simulate the submission and provide the email address for manual contact
      console.log("Form submitted:", formData);

      // In a real implementation, you would send this to a serverless function
      // For now, we'll just show a success message and provide the email
      setSubmitStatus("success");

      // Reset form after successful submission
      setTimeout(() => {
        setFormData({ name: "", email: "", subject: "", message: "" });
        setSubmitStatus(null);
      }, 3000);
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
      setTimeout(() => {
        setSubmitStatus(null);
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOMeta
        title="Contact Us - CyberAnnals | Cybersecurity Archive"
        description="Contact CyberAnnals for questions about our cybersecurity archive. Reach out with feedback or suggestions about our educational content."
        keywords="contact cyberannals, cyberannals contact, contact form, cybersecurity archive contact, cyber security questions, feedback"
        canonical="https://cyberannals.com/contact"
        author="Muhammad Hussain"
        ogType="website"
        ogImage="https://cyberannals.com/cyberAnnals_logo.png"
        structuredData={structuredData}
      />

      {/* Hidden JSON-LD for structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-blue-400 dark:from-blue-400 dark:to-blue-300 bg-clip-text text-transparent">
              Contact Us
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-amber-500 mx-auto rounded-full"></div>
            <p className="text-lg text-slate-600 dark:text-slate-400 mt-6 max-w-2xl mx-auto">
              Have questions about our cybersecurity archive? We'd love to hear from you.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-slate-50 dark:bg-slate-900/50 rounded-xl p-8 border border-slate-200 dark:border-slate-700">
              <h2 className="text-2xl font-bold mb-6 text-blue-600 dark:text-blue-400">
                Send us a Message
              </h2>

              {submitStatus === "success" && (
                <div className="mb-6 p-4 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg">
                  Thank you for your message! We'll get back to you soon.
                </div>
              )}

              {submitStatus === "error" && (
                <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg">
                  Sorry, there was an error sending your message. Please try again or contact us directly at muhammadhucen456@gmail.com
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Subject
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      placeholder="How can we help you?"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors resize-none"
                      placeholder="Tell us about your questions or feedback..."
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full py-3 px-6 rounded-lg font-medium text-white transition-colors ${
                      isSubmitting
                        ? "bg-blue-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </button>
                </div>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div className="bg-slate-50 dark:bg-slate-900/50 rounded-xl p-8 border border-slate-200 dark:border-slate-700">
                <h2 className="text-2xl font-bold mb-6 text-blue-600 dark:text-blue-400">
                  Contact Information
                </h2>

                <div className="space-y-6">
                  <div>
                    <p className="text-slate-600 dark:text-slate-400">
                      Use the contact form on this page to send us a message. We'll respond to your inquiry as soon as possible.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-slate-800 dark:text-slate-200">
                      Social Media
                    </h3>
                    <div className="space-y-3">
                      <a
                        href="http://www.linkedin.com/in/muhammad-hussain456"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                      >
                        <i className="fa-brands fa-linkedin-in text-lg"></i>
                        <span>LinkedIn: muhammad-hussain456</span>
                      </a>
                      <a
                        href="https://x.com/muhammadhu7ain"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                      >
                        <i className="fa-brands fa-twitter text-lg"></i>
                        <span>Twitter: @muhammadhu7ain</span>
                      </a>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-slate-800 dark:text-slate-200">
                      Response Time
                    </h3>
                    <p className="text-slate-600 dark:text-slate-400">
                      We typically respond to messages within 24-48 hours. For urgent matters, please use the direct email link above.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-6 rounded-r-lg">
                <h3 className="font-bold text-lg text-blue-800 dark:text-blue-200 mb-2">
                  About Our Contact Process
                </h3>
                <p className="text-blue-700 dark:text-blue-300 text-sm">
                  Your privacy is important to us. All information shared through our contact form is used solely to respond to your inquiries and improve our educational content. We respect your privacy and do not share your information with third parties.
                </p>
              </div>
            </div>
          </div>

          {/* Navigation Links */}
          <div className="flex flex-wrap justify-center gap-4 pt-12 border-t border-slate-200 dark:border-slate-700 mt-12">
            <Link
              to="/"
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium text-sm"
            >
              Back to Home
            </Link>
            <Link
              to="/about"
              className="px-6 py-3 bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 rounded-lg transition-colors font-medium text-sm"
            >
              About Us
            </Link>
            <Link
              to="/blog"
              className="px-6 py-3 bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 rounded-lg transition-colors font-medium text-sm"
            >
              Explore Blog
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactUs;